/**
 * Internal Userpilot Core Implementation
 *
 * ⚠️ DO NOT IMPORT THIS MODULE DIRECTLY ⚠️
 *
 * This module contains the complete internal implementation for Userpilot:
 * - Initialization and configuration
 * - User identification and session management
 * - Event tracking and survey triggering
 *
 * Only the `useUserpilotInit` and `useUserpilotTracking` hooks should import from this module.
 *
 * For tracking events, always use:
 * ```typescript
 * import { useUserpilotTracking } from '../hooks/useUserpilotTracking';
 * import { TRACK_EVENTS, SURVEY_CODES } from '../userpilot';
 *
 * const { trackEvent, triggerSurvey } = useUserpilotTracking();
 * trackEvent(TRACK_EVENTS.RECORDING_STARTED, { ... });
 * ```
 *
 * @internal
 */

import { logDebug, logError } from '@ptek/client-logger';
import { formatName, type UserInfo } from '@ptek/shared-auth-sdk';
import { ENABLED_BRANDS } from '../index';
import type { BaseEventParams, TrackedEvent } from './types';

const isEnabled = (): boolean =>
  import.meta.env.VITE_USERPILOT_ENABLED === 'true' &&
  Boolean(import.meta.env.VITE_USERPILOT_APP_TOKEN);

export const isEnabledForUser = (user: UserInfo | null): boolean =>
  isEnabled() &&
  user !== null &&
  ENABLED_BRANDS.includes(user.brand as (typeof ENABLED_BRANDS)[number]);

let isInitialized = false;
let userpilotInstance: typeof import('userpilot').Userpilot | null = null;

/**
 * Check if tracking operations can proceed
 * Centralized guard to ensure consistency across all operations
 * @internal
 */
const canTrack = (): boolean => {
  return isInitialized && userpilotInstance !== null && isEnabled();
};

export const initializeUserpilot = async (): Promise<void> => {
  if (isInitialized || !isEnabled()) {
    return;
  }
  const appToken = import.meta.env.VITE_USERPILOT_APP_TOKEN;

  try {
    const { Userpilot } = await import('userpilot');
    Userpilot.initialize(appToken);
    userpilotInstance = Userpilot;
    isInitialized = true;
    logDebug('Userpilot', 'Userpilot initialized successfully');
  } catch (error) {
    logError('Userpilot', 'Failed to initialize Userpilot', error);
  }
};

const createUserpilotUserData = (userInfo: UserInfo) => {
  // Format the email in a way that is consistent with nebula
  const email = userInfo.email?.toLowerCase();
  return {
    userId: userInfo.userId,
    // For classic this will always be defined, but will need to update when integrating with other brands
    email: email || 'UNKNOWN_EMAIL',
    customerId: userInfo.customerId,
    market: userInfo.market,
    brand: userInfo.brand,
    name: formatName(userInfo),

    company: {
      // This is the only company data we have access to right now, compare to (neb-www)/src/library/userpilot/index.js
      id: userInfo.customerId,
    },
  };
};

export const identifyUser = (user: UserInfo): void => {
  if (!isInitialized || !userpilotInstance || !isEnabledForUser(user)) {
    return;
  }

  try {
    const userPilotUserData = createUserpilotUserData(user);

    userpilotInstance.identify(userPilotUserData.email, userPilotUserData);

    logDebug('Userpilot', 'User identified successfully', {
      email: userPilotUserData.email,
      userPilotUserData,
    });
  } catch (error) {
    logError('Userpilot', 'Failed to identify user', error);
  }
};

export const resetUserpilot = (): void => {
  if (!canTrack()) {
    return;
  }

  try {
    userpilotInstance!.reset();
    logDebug('Userpilot', 'User session reset');
  } catch (error) {
    logError('Userpilot', 'Failed to reset Userpilot', error);
  }
};

/**
 * Track an event in Userpilot
 * @param eventName - The name of the event to track
 * @param params - Optional parameters to include with the event
 * @internal
 */
const track = (eventName: string, params?: Record<string, unknown>): void => {
  if (!canTrack()) {
    logDebug(
      'Userpilot',
      'Cannot track event - Userpilot not initialized or not enabled',
      {
        eventName,
        isInitialized,
        hasInstance: Boolean(userpilotInstance),
        isEnabled: isEnabled(),
      }
    );
    return;
  }

  try {
    userpilotInstance!.track(eventName, params);
    logDebug('Userpilot', 'Event tracked successfully', {
      eventName,
      params,
    });
  } catch (error) {
    logError('Userpilot', 'Failed to track event', {
      eventName,
      params,
      error,
    });
  }
};

/**
 * Trigger a Userpilot survey
 * @param surveyCode - The code of the survey to trigger
 * @internal
 */
const trigger = (surveyCode: string): void => {
  if (!canTrack()) {
    logDebug(
      'Userpilot',
      'Cannot trigger survey - Userpilot not initialized or not enabled',
      {
        surveyCode,
        isInitialized,
        hasInstance: Boolean(userpilotInstance),
        isEnabled: isEnabled(),
      }
    );
    return;
  }

  try {
    userpilotInstance!.trigger(surveyCode);
    logDebug('Userpilot', 'Survey triggered successfully', {
      surveyCode,
    });
  } catch (error) {
    logError('Userpilot', 'Failed to trigger survey', {
      surveyCode,
      error,
    });
  }
};

/**
 * Track an event with type-safe parameters
 * Internal helper that adds timestamp and enhanced logging
 *
 * This function provides additional functionality beyond the base `track()`:
 * - Automatic timestamp injection
 * - Enhanced debug logging with event name
 * - Type-safe event parameters
 *
 * Only exported for use by `useUserpilotTracking` hook
 * @internal
 */
export function trackEvent<TParams>(
  event: TrackedEvent<TParams>,
  params?: TParams
): void {
  logDebug('UserpilotTrackV2', `Tracking event: ${event.name}`, params);
  track(event.name, {
    ...params,
    timestamp:
      (params as BaseEventParams)?.timestamp || new Date().toISOString(),
  });
}

/**
 * Trigger a Userpilot survey with enhanced logging
 * Internal helper that adds consistent logging
 *
 * This function provides additional functionality beyond the base `trigger()`:
 * - Enhanced debug logging with survey code
 * - Consistent logging format
 *
 * Only exported for use by `useUserpilotTracking` hook
 * @internal
 */
export function triggerSurvey(surveyCode: string): void {
  logDebug('UserpilotSurveyV2', `Triggering survey: ${surveyCode}`);
  trigger(surveyCode);
}
